import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "../lib/api";
import { Badge } from "../components/UI";
import { Printer, Phone, Mail, MapPin, ShieldCheck, AlertTriangle, Pill, Stethoscope, Users, HeartPulse } from "lucide-react";

const maskSSN = (ssn) => (ssn ? `***-**-${String(ssn).replace(/\D/g, "").slice(-4)}` : "-");

function Section({ icon: Icon, title, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3 font-heading text-sm font-bold text-[#3E2723]">
        <Icon size={15} className="text-[#7B6157]" /> {title}
      </div>
      {children}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 text-sm border-b border-[#F1EEE7] last:border-0">
    <span className="text-[#7B6157] shrink-0">{label}</span>
    <span className="text-[#3E2723] font-medium text-right break-words min-w-0">{value || "-"}</span>
  </div>
);

export function FaceSheet({ client, intake, authorizations }) {
  const [clinician, setClinician] = useState(null);
  const [family, setFamily] = useState([]);
  useEffect(() => {
    if (!client?.clinician_id) { setClinician(null); }
    else api.get("/users").then((r) => setClinician(r.data.find((u) => u.id === client.clinician_id) || null)).catch(() => {});
    api.get(`/clients/${client.id}/family`).then((r) => setFamily(r.data)).catch(() => setFamily([]));
  }, [client?.id, client?.clinician_id]);

  const age = client.dob ? dayjs().diff(dayjs(client.dob), "year") : "-";
  const ins = client.insurance || {};
  const primary = intake?.insurance?.primary || {};
  const contacts = family.filter((m) => m.is_emergency_contact)
    .sort((a, b) => (Number(a.emergency_priority) || 99) - (Number(b.emergency_priority) || 99))
    .map((m) => ({ name: `${m.first_name} ${m.last_name}`.trim(), relationship: m.relationship, cell_phone: m.phone || m.alt_phone }));
  const program = intake?.requested_services?.services?.[0]?.program;
  const auths = (authorizations || []).filter((a) => a.client_id === client.id);

  const doPrint = () => {
    document.body.classList.add("print-face-sheet");
    window.print();
    setTimeout(() => document.body.classList.remove("print-face-sheet"), 300);
  };

  return (
    <div data-print-area data-testid="face-sheet">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-heading text-xl font-bold text-[#3E2723]">{client.first_name} {client.last_name}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-[#6D4C41]">
            <span>DOB {client.dob || "-"} ({age} yrs)</span>
            <span>{client.gender || "-"}</span>
            <span>MRN {client.mrn}</span>
            <span>HRN {client.health_record_number || "-"}</span>
            <Badge value={client.status} />
          </div>
        </div>
        <button type="button" data-testid="print-face-sheet-btn" onClick={doPrint}
          className="print:hidden inline-flex items-center gap-2 rounded-full border border-[#D7CCC8] px-4 py-2 text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB] transition-colors">
          <Printer size={14} /> Print Face Sheet
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Section icon={MapPin} title="Demographics & Contact">
          <Row label="Address" value={[client.address, client.city, client.state, client.zip].filter(Boolean).join(", ")} />
          <Row label="County" value={intake?.demographics?.county} />
          <Row label="Phone" value={client.phone} />
          <Row label="Email" value={client.email} />
          <Row label="SSN" value={maskSSN(intake?.demographics?.ssn)} />
        </Section>

        <Section icon={ShieldCheck} title="Insurance">
          <Row label="Company" value={ins.company_name || primary.company} />
          <Row label="Type" value={primary.type} />
          <Row label="Policy / ID #" value={ins.policy_number || primary.identification_number} />
          <Row label="Group #" value={ins.group_number || primary.group_number} />
          <Row label="Effective" value={ins.effective_date || primary.effective_date} />
        </Section>

        <Section icon={Users} title="Emergency Contacts">
          {contacts.length === 0 ? <div className="text-sm text-[#84675B]">None on file</div> : contacts.map((ct, i) => (
            <div key={i} className="py-1.5 border-b border-[#F1EEE7] last:border-0">
              <div className="text-sm font-semibold text-[#3E2723]">{ct.name} <span className="font-normal text-[#7B6157]">({ct.relationship || "-"})</span></div>
              <div className="text-xs text-[#6D4C41] flex items-center gap-1 mt-0.5"><Phone size={11} /> {ct.cell_phone || ct.home_phone || "-"}</div>
            </div>
          ))}
        </Section>

        <Section icon={Stethoscope} title="Care Team & Admission">
          <Row label="Clinician" value={clinician ? `${clinician.first_name} ${clinician.last_name}` : "Unassigned"} />
          <Row label="Program" value={program} />
          <Row label="Admission Date" value={(client.admission_date || "").slice(0, 10)} />
          <Row label="Last Seen" value={(client.last_seen || "").slice(0, 10)} />
        </Section>

        <Section icon={HeartPulse} title="Diagnosis">
          <div className="text-sm text-[#3E2723] font-medium">{client.diagnosis || "No diagnosis on file"}</div>
          {(intake?.diagnosis?.entries || []).map((e, i) => (
            <div key={i} className="text-sm text-[#6D4C41] mt-1"><span className="font-semibold">{e.code}</span> {e.description}</div>
          ))}
        </Section>

        <Section icon={ShieldCheck} title="Authorizations">
          {auths.length === 0 ? <div className="text-sm text-[#84675B]">None on file</div> : auths.map((a) => (
            <div key={a.id} className="py-1.5 border-b border-[#F1EEE7] last:border-0 text-sm">
              <span className="font-semibold text-[#3E2723]">{a.auth_number}</span> · {a.company}
              <div className="text-xs text-[#6D4C41]">{a.sessions_used}/{a.sessions_approved} sessions · expires {a.expires}</div>
            </div>
          ))}
        </Section>

        <Section icon={AlertTriangle} title="Allergies" className="border-[#FFE0B2] bg-[#FFF8F0]">
          {(client.allergies || []).length === 0 ? <div className="text-sm text-[#84675B]">No known allergies</div> : (
            <div className="flex flex-wrap gap-2">
              {client.allergies.map((a) => <span key={a} className="rounded-full bg-white border border-[#FFE0B2] px-3 py-1 text-xs font-semibold text-[#B84A00]">{a}</span>)}
            </div>
          )}
        </Section>

        <Section icon={Pill} title="Current Medications" className="md:col-span-2 xl:col-span-2">
          {(client.medications || []).length === 0 ? <div className="text-sm text-[#84675B]">No current medications</div> : (
            <div className="flex flex-wrap gap-2">
              {client.medications.map((m) => <span key={m} className="rounded-full bg-white border border-[#E5DFD3] px-3 py-1 text-xs font-medium text-[#4E342E]">{m}</span>)}
            </div>
          )}
        </Section>

        <Section icon={Mail} title="Alerts" className={(client.alerts || []).length ? "border-[#FFCDD2] bg-[#FFF5F5]" : ""}>
          {(client.alerts || []).length === 0 ? <div className="text-sm text-[#84675B]">No active alerts</div> : (client.alerts || []).map((a, i) => (
            <div key={i} className="text-sm text-[#C62828] py-0.5">• {a.text}</div>
          ))}
        </Section>
      </div>

      <div className="mt-4 text-[11px] text-[#84675B]">Generated {dayjs().format("MM/DD/YYYY h:mm A")} · Cocoa EHR - Confidential: contains protected health information (PHI)</div>
    </div>
  );
}
